import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { getItem } from '../../../../utils/localStorage';
import { makeRequest2 } from '../../../../utils/makeRequest';

const TransferCreditDialog = ({
    visible,
    onHide,
    selectedPayment,
    onTransferCredit,
    loading,
    formatCurrency,
    toast
}) => {
    const [targetAccountNumber, setTargetAccountNumber] = useState('');
    const [targetCustomerDetails, setTargetCustomerDetails] = useState(null);
    const [transferAmount, setTransferAmount] = useState('');
    const [comments, setComments] = useState('');
    const [loadingCustomer, setLoadingCustomer] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [availableCredit, setAvailableCredit] = useState(0);
    const [transferType, setTransferType] = useState('credit');
    const [sourceAccountNumber, setSourceAccountNumber] = useState('');
    const [sourceCustomer, setSourceCustomer] = useState(null);
    const [fetchingSourceCustomer, setFetchingSourceCustomer] = useState(false);

    const transferTypes = [
        { label: 'Credit Transfer', value: 'credit' },
        { label: 'Overpayment Transfer', value: 'overpayment' }
    ];

    // Helper to check if an account number has the correct format
    const isValidAccountNumber = (accountNumber) => {
        if (!accountNumber || typeof accountNumber !== 'string') return false;
        // Must be numbers only and start with 5, 6, 7, or 8 (per backend code)
        return /^[5-8]\d+$/.test(accountNumber);
    };

    // Fetch customer details from the customer model
    const fetchCustomerById = async (customerId) => {
        if (!customerId) return null;

        try {
            setFetchingSourceCustomer(true);
            const response = await makeRequest2(
                `/api/app/customer_management/get_customer/${customerId}`,
                'GET'
            );

            if (response && response.success && response.data) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Error fetching customer:', error);
            return null;
        } finally {
            setFetchingSourceCustomer(false);
        }
    };

    useEffect(() => {
        if (visible && selectedPayment) {

            // Get available credit amount from the payment
            const creditAmount = selectedPayment.paymentAmount || selectedPayment.amount || 0;
            setAvailableCredit(creditAmount);

            // Get the source account number (make sure it's a string)
            let accountNum = '';
            if (selectedPayment.accountNumber) {
                accountNum = String(selectedPayment.accountNumber);
            }

            setSourceAccountNumber(accountNum);

            // Reset form
            setTargetAccountNumber('');
            setTargetCustomerDetails(null);
            setTransferAmount('');
            setComments('');
            setErrorMessage('');
            setTransferType('credit');

            // Validate source account
            if (!isValidAccountNumber(accountNum)) {
                setErrorMessage(`Invalid source account number format: ${accountNum}. Account numbers should start with 5, 6, 7, or 8.`);
            }

            // Fetch customer information using customerId if available
            const customerId = selectedPayment.customerId;
            if (customerId) {
                fetchCustomerById(customerId).then(customerData => {
                    if (customerData) {
                        setSourceCustomer(customerData);
                    } else {
                        setSourceCustomer(null);
                    }
                });
            } else {
                setSourceCustomer(null);
            }
        }
    }, [visible, selectedPayment]);

    const searchCustomerByAccount = async () => {
        if (!targetAccountNumber.trim()) {
            setErrorMessage('Please enter an account number');
            return;
        }

        try {
            setLoadingCustomer(true);
            setErrorMessage('');

            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(
                `/api/app/payment_management/get_invoice_by_account/${facilityId}/${targetAccountNumber}`,
                'GET'
            );

            // Handle double-nested data structure
            let customerData = null;
            if (response && response.success) {
                if (response.data && response.data.success && response.data.data) {
                    // Double nested format: response.data.data
                    customerData = response.data.data;
                } else if (response.data) {
                    // Single nested format: response.data
                    customerData = response.data;
                }
            }

            if (customerData) {             

                setTargetCustomerDetails(customerData);
            } else {
                setTargetCustomerDetails(null);
                setErrorMessage('Customer not found with this account number');
            }
        } catch (error) {
            console.error('Error searching customer:', error);
            setTargetCustomerDetails(null);
            setErrorMessage(error.message || 'Error searching for customer');
        } finally {
            setLoadingCustomer(false);
        }
    };

    const handleTransfer = () => {
        if (!isValidAccountNumber(sourceAccountNumber)) {
            setErrorMessage(`Invalid source account number format: ${sourceAccountNumber}. Account numbers should start with 5, 6, 7, or 8.`);
            return;
        }

        if (!targetCustomerDetails) {
            setErrorMessage('Please search for a valid customer account first');
            return;
        }

        if (!transferAmount || isNaN(parseFloat(transferAmount)) || parseFloat(transferAmount) <= 0) {
            setErrorMessage('Please enter a valid transfer amount');
            return;
        }

        const amount = parseFloat(transferAmount);
        if (amount > availableCredit) {
            setErrorMessage(`Transfer amount cannot exceed available credit (${formatCurrency(availableCredit, selectedPayment?.currency?.code)})`);
            return;
        }

        // Ensure the amount doesn't exceed the target invoice balance if it's a payment
        if (targetCustomerDetails.balance && amount > targetCustomerDetails.balance) {
            if (toast) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Amount Warning',
                    detail: `The transfer amount exceeds the target invoice balance of ${formatCurrency(targetCustomerDetails.balance, targetCustomerDetails.currency?.code)}. The maximum necessary amount will be transferred.`,
                    life: 5000
                });
            }
        }

        onTransferCredit({
            amount,
            sourceAccountNumber,
            destinationAccountNumber: targetAccountNumber,
            transferType,
            notes: comments
        });
    };

    const getSourceCustomerName = () => {
        // First check if we have source customer from API
        if (sourceCustomer) {
            return `${sourceCustomer.firstName || ''} ${sourceCustomer.lastName || ''}`.trim();
        }

        // Then check various locations in the selectedPayment
        return selectedPayment?.customer?.name ||
            (selectedPayment?.client?.firstName
                ? `${selectedPayment.client.firstName} ${selectedPayment.client.lastName || ''}`
                : selectedPayment?.customerName || 'Unknown');
    };

    const renderSourceInfo = () => {
        if (!selectedPayment) return null;

        // Get customer name
        const sourceCustomerName = getSourceCustomerName();

        return (
            <div className="p-3 bg-light rounded mb-3">
                <h6 className="border-bottom pb-2 mb-3">Source Information</h6>
                <div className="row">
                    <div className="col-md-6">
                        <p className="mb-2">
                            <strong>Customer:</strong> {fetchingSourceCustomer ?
                                <span><i className="fa fa-spinner fa-spin mr-1"></i>Loading...</span> :
                                sourceCustomerName
                            }
                        </p>
                    </div>
                    <div className="col-md-6">
                        <p className="mb-2">
                            <strong>Reference:</strong> {selectedPayment?.paymentReference || selectedPayment?.mpesaReceiptNumber || 'N/A'}
                        </p>
                    </div>

                    <div className="col-md-6">
                        <p className="mb-2">
                            <strong>Account:</strong> {sourceAccountNumber || 'N/A'}
                            {!isValidAccountNumber(sourceAccountNumber) && (
                                <span className="ml-2 text-danger" title="Invalid account format">
                                    <i className="fa fa-exclamation-triangle"></i>
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="col-md-6">
                        <p className="mb-2">
                            <strong>Credit:</strong> {formatCurrency(availableCredit, selectedPayment?.currency?.code)}
                        </p>
                    </div>

                    {sourceCustomer && (
                        <>
                            <div className="col-md-6">
                                <p className="mb-2"><strong>Phone:</strong> {sourceCustomer.phoneNumber || 'N/A'}</p>
                            </div>
                            <div className="col-md-6">
                                {sourceCustomer.email && (
                                    <p className="mb-0"><strong>Email:</strong> {sourceCustomer.email}</p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderTargetCustomerInfo = () => {
        if (!targetCustomerDetails) return null;

        // Access customer object directly from targetCustomerDetails
        const hasCustomer = !!targetCustomerDetails.customer;

        return (
            <div className="mb-4">
                <div className="alert alert-success p-3">
                    <h6 className="mb-3">Customer Found</h6>
                    <div className="row">
                        <div className="col-md-6">
                            {hasCustomer ? (
                                <>
                                    <p className="mb-2"><strong>Name:</strong> {targetCustomerDetails.customer.name}</p>
                                    <p className="mb-2"><strong>Phone:</strong> {targetCustomerDetails.customer.phoneNumber || 'N/A'}</p>
                                    {targetCustomerDetails.customer?.email && (
                                        <p className="mb-2"><strong>Email:</strong> {targetCustomerDetails.customer.email}</p>
                                    )}
                                </>
                            ) : (
                                <p className="mb-2 text-warning">Customer information not available</p>
                            )}
                        </div>
                        <div className="col-md-6">
                            <p className="mb-2"><strong>Invoice #:</strong> {targetCustomerDetails.invoiceNumber}</p>
                            <p className="mb-2"><strong>Status:</strong> {targetCustomerDetails.status}</p>
                            <p className="mb-2">
                                <strong>Balance:</strong> {formatCurrency(targetCustomerDetails.balance, targetCustomerDetails.currency?.code)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="row mt-3">
                    <div className="col-md-6">
                        <label className="form-label">Transfer Type <span className="text-danger">*</span></label>
                        <Dropdown
                            value={transferType}
                            options={transferTypes}
                            onChange={(e) => setTransferType(e.value)}
                            className="w-100"
                        />
                        <small className="text-muted d-block mt-1">
                            Select credit transfer for regular transfers or overpayment for excess payments
                        </small>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Transfer Amount <span className="text-danger">*</span></label>
                        <InputText
                            className="form-control"
                            value={transferAmount}
                            onChange={(e) => setTransferAmount(e.target.value)}
                            placeholder={`Max: ${formatCurrency(availableCredit, selectedPayment?.currency?.code)}`}
                            keyfilter="money"
                        />
                        <small className="text-muted d-block mt-1">
                            Available: {selectedPayment ? formatCurrency(availableCredit, selectedPayment?.currency?.code) : ''}
                        </small>
                        {targetCustomerDetails.balance && (
                            <small className="text-info d-block mt-1">
                                Target Balance: {formatCurrency(targetCustomerDetails.balance, targetCustomerDetails.currency?.code)}
                            </small>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Dialog
            header="Transfer Credit"
            visible={visible}
            onHide={onHide}
            className="transfer-credit-dialog"
            style={{ width: '650px' }}
            modal
            closeOnEscape
            blockScroll
        >
            <div className="p-3">
                {renderSourceInfo()}

                <div className="mb-4">
                    <div className="row">
                        <div className="col-md-12">
                            <label className="form-label">Target Account Number <span className="text-danger">*</span></label>
                            <div className="input-group">
                                <InputText
                                    className="form-control"
                                    value={targetAccountNumber}
                                    onChange={(e) => setTargetAccountNumber(e.target.value)}
                                    placeholder="Enter customer account number"
                                />
                                <button
                                    className="btn btn-primary"
                                    type="button"
                                    onClick={searchCustomerByAccount}
                                    disabled={loadingCustomer || !targetAccountNumber.trim()}
                                >
                                    {loadingCustomer ? <span className="spinner-border spinner-border-sm"></span> : 'Search'}
                                </button>
                            </div>
                            <small className="text-muted d-block mt-1">
                                Enter the account number of the invoice where you want to transfer credit
                            </small>
                        </div>
                    </div>
                </div>

                {errorMessage && (
                    <div className="alert alert-danger mb-4">
                        {errorMessage}
                    </div>
                )}

                {renderTargetCustomerInfo()}

                <div className="mb-4">
                    <label className="form-label">Comments</label>
                    <InputTextarea
                        className="form-control"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        rows={3}
                        placeholder="Add any notes about this transfer (optional)"
                    />
                </div>

                <div className="d-flex justify-content-end">
                    <button
                        type="button"
                        className="btn btn-secondary mr-2"
                        onClick={onHide}
                        style={{ marginRight: '10px' }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleTransfer}
                        disabled={loading || !targetCustomerDetails || !transferAmount || !isValidAccountNumber(sourceAccountNumber)}
                    >
                        {loading ? <span className="spinner-border spinner-border-sm mr-2"></span> : ''}
                        Transfer Credit
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default TransferCreditDialog;